import React, { useState, useEffect } from "react";
import StarRatingComponent from "react-star-rating-component";
import { FormGroup, Form, FormFile, Row, Col } from "react-bootstrap";
import ImageUploader from "react-images-upload";

import * as S from "./styles";
import { IProps } from "./types";
import { Button } from "@atoms";
import { useMutation } from "@apollo/react-hooks";
import {
  REVIEW_CREATE,
  REVIEW_IMAGES_CREATE,
} from "@organisms/WriteReview/queries";
import { reviewCreate } from "@organisms/WriteReview/__generated__/reviewCreate";
import { reviewImagesCreate } from "@organisms/WriteReview/__generated__/reviewImagesCreate";
import { useAlert } from "react-alert";
import { ProductVariantSelectBox } from "@components/ProductPage/ProductDetailVariantSelection";
import { SizedBox } from "@components/CommonStyledComponents";

const WriteReview: React.FC<IProps> = ({
  productId,
  setShowWriteReview,
  setReviewStatus,
  sortedVariants=null,
  selectedVariantForReview,
  setSelectedVariantForReview,
  showVariants=false,
}: IProps) => {
  const [reviewStarValue, setReviewStarValue] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewImages, setReviewImages] = useState([]);

  const handleStarClick = (nextValue: number) => {
    setReviewStarValue(nextValue);
  };

  const [disabled, setDisabled] = useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  useEffect(() => {
    if (5 < reviewImages.length) {
      //TODO: validation message
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [reviewImages]);

  const onDrop = (pictures: File[]) => {
    setReviewImages([...pictures]);
  };

  const alert = useAlert();
  const [
    createReview,
    { data: dataReview, error: errorReview, loading: errorLoading },
  ] = useMutation<reviewCreate>(REVIEW_CREATE, {
    onCompleted: (data) => {
      if (reviewImages.length) {
        const reviewId = data.reviewCreate.productReview.id;
        createReviewImages({
          variables: {
            input: {
              review: reviewId,
              images: reviewImages,
            },
          },
        });
      }
      if (data.reviewCreate.errors.length === 0) {
        alert.success(
          "Thank you !\nWe are grateful for your feedback!\n" +
            "Your Maccaron Points will reflect in your account once " +
            "approved internally."
        );
        setShowWriteReview(false);
        setReviewStatus("PENDING");
      } else {
        alert.error(data.reviewCreate.errors[0].message)
      }
      setIsLoading(false);
    },
  });

  const [
    createReviewImages,
    { data, error, loading },
  ] = useMutation<reviewImagesCreate>(REVIEW_IMAGES_CREATE, {
    onCompleted: (data) => {
      if (data.reviewImagesCreate.errors.length === 0) {
        alert.success("Review Images Successfully Uploaded.");
        setReviewImages([]);
      }
    },
  });

  const handleClickSubmit = (e: any) => {
    if (reviewTitle === "") {
      alert.error("Title should not be empty.");
      return;
    }
    if (reviewComment === "") {
      alert.error("Review should not be empty.");
      return;
    }
    if (5 < reviewImages.length) {
      alert.error(
        "Too many images are being saved.\nYou can upload 5 images at most."
      );
      return;
    }
    createReview({
      variables: {
        input: {
          comment: reviewComment,
          product: productId,
          rating: reviewStarValue,
          title: reviewTitle,
          variant: sortedVariants === null ? null : sortedVariants[selectedVariantForReview]?.id,
        },
      },
    });
    setIsLoading(true);
  };

  const handleChange = (index) => {
    if (setSelectedVariantForReview !== null) {
      setSelectedVariantForReview(index);
    }
  };

  return (
    <S.Wrapper>
      <S.WrapperRow>
        <S.WrapperCol>
          <S.InnerRow>
            <S.InnerCol>
              <span>WRITE REVIEW</span>
            </S.InnerCol>
          </S.InnerRow>
          <SizedBox height={"0.4rem"}/>
          <S.InnerRow>
            <S.InnerCol md={2}>
              <span>Score</span>
            </S.InnerCol>
          </S.InnerRow>
          <S.InnerRow>
            <S.InnerCol md={6}>
              <S.StarRatingWrapper>
                <StarRatingComponent
                  name="ReviewRating"
                  editing={true}
                  starCount={5}
                  starColor="#F39"
                  emptyStarColor="#BFBFBF"
                  value={reviewStarValue}
                  onStarClick={handleStarClick}
                />
              </S.StarRatingWrapper>
            </S.InnerCol>
          </S.InnerRow>
          {
            showVariants && (
              <>
                <SizedBox height={"0.4rem"}/>
                <Row>
                  <Col>
                    <span>Select Variant</span>
                    <ProductVariantSelectBox
                      sortedVariants={sortedVariants}
                      selectedVariant={selectedVariantForReview}
                      handleChange={handleChange}
                      disableOutOfStock={false}
                    />
                  </Col>
                </Row>
                <SizedBox height={"0.7rem"}/>
              </>
            )
          }
          <FormGroup>
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={reviewTitle}
              onChange={(e: any) => {
                setReviewTitle(e.target.value);
              }}
              autoFocus
            />
          </FormGroup>
          <FormGroup>
            <Form.Label>Review</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={reviewComment}
              onChange={(e: any) => {
                setReviewComment(e.target.value);
              }}
            />
          </FormGroup>
          <div>image uploader</div>
          <FormGroup>
            <ImageUploader
              withIcon={true}
              onChange={onDrop}
              imgExtension={[".jpg", ".png", ".jpeg"]}
              maxFileSize={10485760}
              label="Max file size: 10MB, Accepted: jpg, png"
              withPreview={true}
            />
          </FormGroup>
          <Button fullWidth onClick={handleClickSubmit} disabled={disabled || isLoading}>
            Submit
          </Button>
        </S.WrapperCol>
      </S.WrapperRow>
    </S.Wrapper>
  );
};

export { WriteReview };
